import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UtensilsCrossed, ShoppingCart, DollarSign, BarChart3, Settings, Menu, X, LogOut, User, Users, UserCog, StickyNote, RefreshCw, KeyRound, Calculator, Building2, ClipboardList, PiggyBank } from 'lucide-react';
import { useCafe } from '../context/CafeContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import NotesModal from './NotesModal';
import InstallButton from './InstallButton';
import ChangePasswordModal from './ChangePasswordModal';
import LanguageToggle from './LanguageToggle';

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeOrders, fetchItems, fetchActiveOrders, fetchCounterSession } = useCafe();
  const { user, logout, tenantId: scopedTenantId, tenantName: scopedTenantName, setTenantId } = useAuth();
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesCount, setNotesCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const totalActiveOrders = activeOrders.length;

  // Get notes count from localStorage
  const updateNotesCount = () => {
    try {
      const notes = JSON.parse(localStorage.getItem('tomorrowNotes') || '[]');
      setNotesCount(notes.length);
    } catch {
      setNotesCount(0);
    }
  };

  // Update notes count on component mount and when modal closes
  useEffect(() => {
    updateNotesCount();
  }, []);

  const handleNotesModalClose = () => {
    setNotesModalOpen(false);
    updateNotesCount(); // Refresh count when modal closes
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Leaving a business is a super admin's way back to their own console. It has
  // to clear the scope as well as navigate: the id rides on every request, and
  // a super admin left pointing at a shop they thought they had closed would go
  // on reading and writing that shop's data.
  const handleExitTenant = () => {
    setTenantId(null);
    setMobileMenuOpen(false);
    navigate('/');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // The counter goes with them: it can be opened or closed from another
      // till, and a stale session here blocks or misdirects the next order
      await Promise.all([fetchItems(), fetchActiveOrders(), fetchCounterSession()]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Staff accounts expose everyone's salary, so it is owner-level only. The
  // API enforces the same roles - this just keeps the tab out of the way.
  const canManageStaff = user?.role === 'super_admin' || user?.role === 'tenant_admin';
  const canManageUsers = user?.role === 'super_admin' || user?.role === 'tenant_admin';

  // A super admin's home is the system console, so their "Dashboard" tab points
  // at the till dashboard explicitly and a Businesses tab takes its place at /.
  const isSuperAdmin = user?.role === 'super_admin';

  // A sales agent has one screen and one job: register businesses. They belong
  // to no tenant, so every till tab would only ever load an error - the whole
  // menu collapses to the one page they can actually use.
  const isSalesAgent = user?.role === 'sales';

  // A super admin runs the businesses, not a till. Until they open one, the
  // till tabs have no tenant to load and every one of them lands on an empty
  // screen - so their menu is just Businesses, which is also where the global
  // default menu is managed. Opening a business scopes every request to it and
  // brings the full menu back, exactly as that shop's own staff would see it.
  const isTenantScoped = !isSuperAdmin || Boolean(scopedTenantId);
  const showTillNav = !isSalesAgent && isTenantScoped;

  const businessesTab = { id: 'businesses', path: '/', label: 'Businesses', icon: Building2 };

  // `label` stays English - it is the translation key, and t() turns it into
  // Nepali only at render time. Anything that has to identify a tab in code
  // matches on `id`, never on the label, so it keeps working in either language.
  const navItems = !showTillNav
    ? [businessesTab]
    : [
        ...(isSuperAdmin ? [businessesTab] : []),
        { id: 'dashboard', path: isSuperAdmin ? '/dashboard' : '/', label: 'Dashboard', icon: UtensilsCrossed },
        { id: 'orders', path: '/orders', label: 'Orders', icon: ShoppingCart },
        { id: 'counter', path: '/counter', label: 'Counter', icon: Calculator },
        { id: 'dues', path: '/dues', label: 'Dues', icon: ClipboardList },
        { id: 'expenses', path: '/expenses', label: 'Expenses', icon: DollarSign },
        { id: 'savings', path: '/savings', label: 'Savings', icon: PiggyBank },
        ...(canManageStaff ? [{ id: 'staff', path: '/staff', label: 'Staff', icon: UserCog }] : []),
        { id: 'reports', path: '/reports', label: 'Reports', icon: BarChart3 },
        { id: 'settings', path: '/settings', label: 'Settings', icon: Settings },
        ...(canManageUsers ? [{ id: 'users', path: '/users', label: 'User Management', icon: Users }] : []),
      ];

  // Bottom navigation - five slots, so it carries what is touched during a
  // shift. Savings takes the Reports slot: money is put by at the counter,
  // several times a week, while reports are read sitting down and are one tap
  // away in the menu above.
  const bottomNavItems = [
    { id: 'dashboard', path: isSuperAdmin ? '/dashboard' : '/', label: 'Dashboard', icon: UtensilsCrossed },
    { id: 'orders', path: '/orders', label: 'Orders', icon: ShoppingCart },
    { id: 'expenses', path: '/expenses', label: 'Expenses', icon: DollarSign },
    { id: 'savings', path: '/savings', label: 'Savings', icon: PiggyBank },
    { id: 'notes', path: '#', label: 'Notes', icon: StickyNote, onClick: () => setNotesModalOpen(true) }
  ];

  return (
    <div className="min-h-[100dvh] bg-cafe-50">
      {/* One slim row: brand and identity on the left, actions on the right.
          The old header spent 64px on a display-size gradient title and a
          separate user block - the same information now fits in a 56px bar,
          with who-you-are folded under the shop name. */}
      <header className="bg-white/95 backdrop-blur shadow-sm border-b border-cafe-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 gap-2">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <div className="p-1.5 bg-gradient-to-br from-cafe-500 to-cafe-700 rounded-lg shadow-sm flex-shrink-0">
                <UtensilsCrossed className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 leading-tight truncate">
                  {user?.tenantName || t('Cafe Manager')}
                </h1>
                <p className="hidden sm:block text-[11px] text-gray-400 leading-tight truncate">
                  {user?.username}
                  {user?.role === 'super_admin'
                    ? ` · ${t('Super Admin')}`
                    : isSalesAgent
                      ? ` · ${t('Sales')}`
                      : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
              {/* Language switch. Icon-only on phones, where the header is
                  already tight; the full native name shows from sm up. */}
              <LanguageToggle className="hidden sm:flex" />
              <LanguageToggle className="sm:hidden" showLabel={false} />

              {/* PWA Install Button - icon-only on phones, where it matters most */}
              <InstallButton />

              {/* Refresh Button. It reloads menu, orders and till session -
                  none of which an agent has, and none of which a super admin
                  has either until they open a business - so it is theirs to
                  skip. */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                hidden={!showTillNav}
                className="p-2 min-h-[40px] min-w-[40px] flex items-center justify-center text-cafe-600 hover:text-cafe-700 hover:bg-cafe-50 rounded-lg transition-colors flex-shrink-0"
                title={t('Refresh Data')}
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>

              {/* Change Password - Hidden on mobile, shown on sm+ */}
              <button
                onClick={() => setPasswordModalOpen(true)}
                className="hidden sm:flex p-2 min-h-[40px] min-w-[40px] items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                title={t('Change Password')}
              >
                <KeyRound className="h-4 w-4" />
              </button>

              {/* Logout Button - Hidden on mobile, shown on sm+ */}
              <button
                onClick={handleLogout}
                className="hidden sm:flex p-2 min-h-[40px] min-w-[40px] items-center justify-center text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                title={t('Logout')}
              >
                <LogOut className="h-4 w-4" />
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-cafe-500"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Standing reminder that a super admin is inside someone else's business.
          Every order, expense and menu change made from here is written to that
          shop, so it stays on screen for as long as the scope does. */}
      {isSuperAdmin && scopedTenantId && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center min-w-0 text-sm text-blue-900">
              <Building2 className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">
                {t('Viewing')} <span className="font-semibold">{scopedTenantName || scopedTenantId}</span>
              </span>
            </div>
            <button
              onClick={handleExitTenant}
              className="flex items-center flex-shrink-0 px-3 py-1.5 min-h-[36px] text-sm font-medium text-blue-800 bg-white border border-blue-300 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <X className="h-4 w-4 mr-1" />
              {t('Exit')}
            </button>
          </div>
        </div>
      )}

      {/* Desktop Navigation - compact pills on one slim row. It scrolls
          sideways for the same reason the old tabs did: a super admin's set is
          wider than a tablet. The Orders pill carries the live token count so
          it is visible without the mobile tab bar. */}
      <nav className="hidden md:block bg-white border-b border-cafe-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 py-1.5 overflow-x-auto no-scrollbar">
            {navItems.map(({ id, path, label, icon: Icon }) => {
              const isActive = location.pathname === path;

              return (
                <Link
                  key={`desktop-${path}`}
                  to={path}
                  className={`flex items-center flex-shrink-0 whitespace-nowrap gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-cafe-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-cafe-700 hover:bg-cafe-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(label)}
                  {id === 'orders' && totalActiveOrders > 0 && (
                    <span
                      className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${
                        isActive ? 'bg-white text-cafe-700' : 'bg-red-500 text-white'
                      }`}
                    >
                      {totalActiveOrders}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Menu - two columns, so the full set fits on one
          screen without scrolling past a tall single-file list. */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-cafe-100 shadow-lg">
          <div className="px-3 py-3 space-y-3">
            {/* Who is signed in, with the two account actions beside it */}
            <div className="flex items-center justify-between gap-2 px-1">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-cafe-500 to-cafe-700 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{user?.username}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.role === 'super_admin'
                      ? t('Super Admin')
                      : isSalesAgent
                        ? t('Sales')
                        : user?.tenantName}
                  </p>
                </div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setPasswordModalOpen(true);
                  }}
                  className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
                  title={t('Change Password')}
                  aria-label={t('Change Password')}
                >
                  <KeyRound className="h-4 w-4" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('Logout')}
                  aria-label={t('Logout')}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              {navItems.map(({ path, label, icon: Icon }) => {
                const isActive = location.pathname === path;

                return (
                  <Link
                    key={`mobile-menu-${path}`}
                    to={path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center px-3 py-2.5 min-h-[44px] text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-cafe-100 text-cafe-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="truncate">{t(label)}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation for Mobile. Every slot in it is a till screen, so an
          agent - and a super admin who has not opened a business - gets no tab
          bar at all rather than a row of dead ends. */}
      <div
        className={`${showTillNav ? 'md:hidden' : 'hidden'} fixed bottom-0 left-0 right-0 bg-white border-t border-cafe-100 z-50`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="grid grid-cols-5 h-16">
          {bottomNavItems.map(({ id, path, label, icon: Icon, onClick }) => {
            const isActive = location.pathname === path;
            
            if (onClick) {
              return (
                <button
                  key={`bottom-nav-${id}`}
                  onClick={onClick}
                  className={`flex flex-col items-center justify-center space-y-1 transition-colors relative ${
                    isActive
                      ? 'text-cafe-600 bg-cafe-50'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-xs font-medium">{t(label)}</span>
                  {id === 'notes' && notesCount > 0 && (
                    <div className="absolute -top-1 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {notesCount}
                    </div>
                  )}
                </button>
              );
            }
            
            return (
              <Link
                key={`bottom-nav-${path}`}
                to={path}
                className={`flex flex-col items-center justify-center space-y-1 transition-colors relative ${
                  isActive
                    ? 'text-cafe-600 bg-cafe-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{t(label)}</span>
                {id === 'orders' && totalActiveOrders > 0 && (
                  <div className="absolute -top-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {totalActiveOrders}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      {/* The bottom padding clears the mobile tab bar. It has to be a class, not
          an inline style: an inline paddingBottom beats every breakpoint, so
          md:pb-8 never applied and tablets carried 80px of dead space. */}
      <main
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:pb-8 ${
          showTillNav ? 'pb-[calc(5rem+env(safe-area-inset-bottom))]' : 'pb-8'
        }`}
      >
        {children}
      </main>

      {/* Notes Modal */}
      <NotesModal
        isOpen={notesModalOpen}
        onClose={handleNotesModalClose}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </div>
  );
}

export default Layout;
